import { toast } from '@/components/toast';
import { apiBlob } from './api';
import { storedFileUrl } from './portal';

export type PdfMode = 'preview' | 'print' | 'download';

/**
 * Preview (new tab), print (the browser's print dialog over a hidden frame) or download a PDF the
 * API renders. Errors become a toast.
 */
export async function pdfAction(
  mode: PdfMode,
  path: string,
  fileName: string,
  init?: RequestInit,
): Promise<void> {
  return openUrl(mode, fileName, async () => {
    const url = URL.createObjectURL(await apiBlob(path, init));
    return { url, revoke: () => URL.revokeObjectURL(url) };
  });
}

/**
 * Preview or download a stored upload (inbox, attachments): straight from S3 through a signed
 * link when files are in S3, else through the API like pdfAction.
 */
export function storedFileAction(
  mode: 'preview' | 'download',
  orgId: string,
  file: { id: string; fileName: string },
): Promise<void> {
  return openUrl(mode, file.fileName, () => storedFileUrl(orgId, file.id, mode === 'download'));
}

async function openUrl(
  mode: PdfMode,
  fileName: string,
  load: () => Promise<{ url: string; revoke: () => void }>,
): Promise<void> {
  // open the tab before awaiting, so the browser treats it as the user's action
  const win = mode === 'preview' ? window.open('', '_blank') : null;
  try {
    const { url, revoke } = await load();
    if (mode === 'preview') {
      if (win) win.location.href = url;
      else window.location.assign(url);
    } else if (mode === 'download') {
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } else {
      const frame = document.createElement('iframe');
      frame.style.position = 'fixed';
      frame.style.width = '0';
      frame.style.height = '0';
      frame.style.border = '0';
      frame.title = fileName;
      frame.onload = () => {
        frame.contentWindow?.focus();
        frame.contentWindow?.print();
      };
      frame.src = url;
      document.body.appendChild(frame);
      setTimeout(() => frame.remove(), 120_000);
    }
    setTimeout(revoke, 120_000);
  } catch (e) {
    win?.close();
    toast(e instanceof Error ? e.message : String(e), { tone: 'error' });
  }
}

/** File name of a sales document's PDF, as the API names it. */
export function pdfFileName(label: string, number: string | null): string {
  return `${label.replace(/\s/g, '-')}-${number ?? 'draft'}.pdf`;
}
